import React from "react";
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { RiDeleteBin5Line } from "react-icons/ri";
import Modal from "../../components/common/modal";
import Select from "react-select";
import countryList from "react-select-country-list";
import Toggle from "../../components/common/toggle";
import { useViewMedia } from "../../hooks/media/media.view";
import { useMediaUpdate } from "../../hooks/media/media.update";
import { useViewIndustryList } from "../../hooks/industries/industrylist";
import { useViewTagsList } from "../../hooks/tags/tags.list";
import toast from "react-hot-toast";
import "../../assets/styles/createproduct.css";


const options = countryList().getData();

const EditMultimedia = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Hooks
  const { data: mediaData, isLoading: isMediaLoading } = useViewMedia(id);
  const { mutate: updateMedia, isLoading: isUpdating } = useMediaUpdate();
  const { data: industryList, isLoading: isIndustryLoading } = useViewIndustryList();
  const { data: tagsList, isLoading: isTagsLoading } = useViewTagsList();

  // State
  const [imagePreview, setImagePreview] = useState();
  const [thumbnailPreview, setThumbnailPreview] = useState();
  const [selectedFileType, setSelectedFileType] = useState("image");
  const [imageFile, setImageFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [bulkVideos, setBulkVideos] = useState([]);
  const [videoUrl, setVideoUrl] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState("");
  const [selectedIndustry, setSelectedIndustry] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [value, setValue] = useState(null);
  const [status, setStatus] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [isFileDragging, setIsFileDragging] = useState(false);


  // Load existing media data
  useEffect(() => {
    console.log("Raw mediaData:", mediaData);
    console.log("Media object:", mediaData?.data);
    
    if (mediaData?.data) {
      const media = mediaData.data;
      
      console.log("Loading media data:", media);
      console.log("Media type:", media.type);
      console.log("Media status:", media.status);
      
      setTitle(media.title || "");
      setDescription(media.description || "");
      setSelectedFileType(media.type || "image");
      setStatus(media.status === "active" || media.status === true ? true : false);
      
      // Set industry
      if (media.industry?._id) {
        setSelectedIndustry(media.industry._id);
      }
      
      // Set tag
      if (media.tags?._id) {
        setSelectedTag(media.tags._id);
      }
      
      // Set country
      if (media.country) {
        const countryOption = options.find(opt => opt.value === media.country || opt.label === media.country);
        setValue(countryOption || null);
      }
      
      // Set existing file preview based on type
      if (media.type === "image" && media.url) {
        // Construct full image URL
        const imageUrl = media.url.startsWith('http') 
          ? media.url 
          : `${process.env.REACT_APP_BASE_IMAGE || ''}${media.url}`;
        setImagePreview(imageUrl);
      } else if (media.type === "pdf" && media.url) {
        const file = {
          id: 1,
          name: media.fileInfo?.originalName || media.title || "Existing PDF",
          size: media.fileInfo?.sizeInMB || 0,
          progress: 100,
          status: "uploaded",
          timeLeft: 0,
        };
        setUploadedFiles([file]);
      } else if (media.type === "video" && media.url) {
        setBulkVideos([media.url]);
      }
      
      // Set thumbnail if exists
      if (media.thumbnail_image) {
        const thumbnailUrl = media.thumbnail_image.startsWith('http')
          ? media.thumbnail_image
          : `${process.env.REACT_APP_BASE_IMAGE || ''}${media.thumbnail_image}`;
        setThumbnailPreview(thumbnailUrl);
      }
    }
  }, [mediaData]);

  const readThumbnailURL = (input) => {
    if (input.files && input.files[0]) {
      const file = input.files[0];
      setThumbnailFile(file);
      const reader = new FileReader();
      reader.onload = function (e) {
        setThumbnailPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const getEmbedUrl = (url) => {
    if (
      typeof url === "string" &&
      (url.includes("youtube.com") || url.includes("youtu.be"))
    ) {
      let videoId = "";
      if (url.includes("watch?v=")) {
        videoId = url.split("watch?v=")[1].split("&")[0];
      } else if (url.includes("youtu.be/")) {
        videoId = url.split("youtu.be/")[1].split("?")[0];
      }
      return `https://www.youtube.com/embed/${videoId}`;
    }
    return null;
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));
    if (imageFiles.length > 0) {
      const file = imageFiles[0];
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleAddVideoUrl = (e) => {
    e.preventDefault();
    if (videoUrl.trim()) {
      setBulkVideos([videoUrl]); // Only one video
      setVideoUrl("");
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleVideoClick = (vid, index) => {
    setSelectedVideo(vid);
    setSelectedVideoIndex(index);
    setIsVideoModalOpen(true);
  };

  const closeVideoModal = () => {
    setIsVideoModalOpen(false);
    setSelectedVideo(null);
    setSelectedVideoIndex(null);
  };

  const handleDeleteVideo = (indexToDelete) => {
    setBulkVideos((prev) => prev.filter((_, index) => index !== indexToDelete));
  };

  const handleFileDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFileDragging(true);
  };

  const handleFileDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFileDragging(false);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFileDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter(
      (file) =>
        file.type === "application/pdf" ||
        file.type === "application/msword" ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    );

    if (validFiles.length > 0 && validFiles[0]) {
      const file = validFiles[0];
      setPdfFile(file);
      const newFile = {
        id: Date.now(),
        name: file.name,
        size: (file.size / (1024 * 1024)).toFixed(2),
        progress: 100,
        status: "uploaded",
        timeLeft: 0,
      };
      setUploadedFiles([newFile]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setPdfFile(file);
      const newFile = {
        id: Date.now(),
        name: file.name,
        size: (file.size / (1024 * 1024)).toFixed(2),
        progress: 100,
        status: "uploaded",
        timeLeft: 0,
      };
      setUploadedFiles([newFile]);
    }
  };

  const handleRemoveFile = (id) => {
    setUploadedFiles((prev) => prev.filter((file) => file.id !== id));
    setPdfFile(null);
  };

  const handleToggleStatus = (newStatus) => {
    const previousStatus = status;
    const nextStatus = newStatus ? "active" : "inactive";

    // Optimistic update for immediate UI feedback.
    setStatus(newStatus);

    updateMedia(
      { id, body: { status: nextStatus } },
      {
        onError: (error) => {
          setStatus(previousStatus);
          toast.error(error?.response?.data?.message || "Failed to update status");
        },
      },
    );
  };

  const saveData = () => {
    const formData = new FormData();

    try {
      // Add type FIRST
      formData.append('type', selectedFileType);

      // Add text fields
      if (title) formData.append('title', title);
      if (description) formData.append('description', description);
      if (selectedIndustry) formData.append('industry', selectedIndustry);
      if (value) formData.append('country', value.value || value.label);
      if (selectedTag) formData.append('tags', selectedTag);
      formData.append('status', status ? 'active' : 'inactive');
      
      console.log("Submitting FormData with:");
      console.log("- Type:", selectedFileType);
      console.log("- Title:", title);
      console.log("- Status:", status ? 'active' : 'inactive');
      console.log("- Industry:", selectedIndustry);
      console.log("- Tag:", selectedTag);

      // Add files based on type
      if (selectedFileType === 'image') {
        if (imageFile) {
          formData.append('file', imageFile);
        }
      } else if (selectedFileType === 'pdf') {
        if (pdfFile) {
          formData.append('file', pdfFile);
        }
        if (thumbnailFile) {
          formData.append('thumbnail_image', thumbnailFile);
        }
      } else if (selectedFileType === 'video') {
        if (bulkVideos.length > 0) {
          bulkVideos.forEach((url) => {
            formData.append('url', url);
          });
        }
        if (thumbnailFile) {
          formData.append('thumbnail_image', thumbnailFile);
        }
      }

      // Submit
      updateMedia({ id, body: formData }, {
        onSuccess: () => {
          toast.success('Media updated successfully!');
          navigate('/mediauploads');
        },
        onError: (error) => {
          toast.error(error?.response?.data?.message || 'Failed to update media');
        }
      });
    } catch (error) {
      toast.error('An error occurred while updating');
    }
  };

  const industryOptions = industryList?.data || [];
  const tagsOptions = tagsList?.data || [];

  if (isMediaLoading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', fontSize: '16px', color: '#666' }}>
        Loading media data...
      </div>
    );
  }

  if (!mediaData?.data) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', fontSize: '16px', color: '#e74c3c' }}>
        Media not found
      </div>
    );
  }

  return (
    <>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '20px'
      }}>
        {/* Basic Information */}
        <div style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '20px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          border: '1px solid #f0f0f0'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px'
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '500',
              color: '#333',
              margin: 0
            }}>Edit Media</h2>

            {/* Status Toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '14px', fontWeight: '500', color: '#666' }}>
                Status:
              </span>
              <Toggle
                checked={status}
                onChange={handleToggleStatus}
              />
              <span style={{ fontSize: '14px', color: status ? '#27ae60' : '#95a5a6' }}>
                {status ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          {/* File Type Selector */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#666',
              marginBottom: '12px'
            }}>Media Type *</label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '12px'
            }}>
              <button
                type="button"
                onClick={() => setSelectedFileType('image')}
                style={{
                  padding: '16px 24px',
                  border: selectedFileType === 'image' ? '2px solid #004996' : '2px solid #e0e0e0',
                  borderRadius: '8px',
                  background: selectedFileType === 'image' ? '#e3f2fd' : '#fff',
                  color: selectedFileType === 'image' ? '#004996' : '#666',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (selectedFileType !== 'image') {
                    e.currentTarget.style.borderColor = '#004996';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedFileType !== 'image') {
                    e.currentTarget.style.borderColor = '#e0e0e0';
                  }
                }}
              >
                📷 Image
              </button>
              <button
                type="button"
                onClick={() => setSelectedFileType('pdf')}
                style={{
                  padding: '16px 24px',
                  border: selectedFileType === 'pdf' ? '2px solid #004996' : '2px solid #e0e0e0',
                  borderRadius: '8px',
                  background: selectedFileType === 'pdf' ? '#e3f2fd' : '#fff',
                  color: selectedFileType === 'pdf' ? '#004996' : '#666',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (selectedFileType !== 'pdf') {
                    e.currentTarget.style.borderColor = '#004996';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedFileType !== 'pdf') {
                    e.currentTarget.style.borderColor = '#e0e0e0';
                  }
                }}
              >
                📄 PDF
              </button>
              <button
                type="button"
                onClick={() => setSelectedFileType('video')}
                style={{
                  padding: '16px 24px',
                  border: selectedFileType === 'video' ? '2px solid #004996' : '2px solid #e0e0e0',
                  borderRadius: '8px',
                  background: selectedFileType === 'video' ? '#e3f2fd' : '#fff',
                  color: selectedFileType === 'video' ? '#004996' : '#666',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (selectedFileType !== 'video') {
                    e.currentTarget.style.borderColor = '#004996';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedFileType !== 'video') {
                    e.currentTarget.style.borderColor = '#e0e0e0';
                  }
                }}
              >
                🎥 Video
              </button>
            </div>
          </div>

          {/* Title and Description */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: '24px',
            marginTop: '24px'
          }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#666',
                marginBottom: '8px'
              }}>Title *</label>
              <input
                type="text"
                placeholder="Enter media title..."
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '400',
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = '#004996'}
                onBlur={(e) => e.currentTarget.style.borderColor = '#e0e0e0'}
              />
            </div>
          </div>

          <div style={{ marginTop: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#666',
              marginBottom: '8px'
            }}>Description</label>
            <textarea
              placeholder="Enter description..."
              rows="4"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '400',
                outline: 'none',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = '#004996'}
              onBlur={(e) => e.currentTarget.style.borderColor = '#e0e0e0'}
            />
          </div>

          {/* Industry and Country Selection */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '20px',
            marginTop: '20px'
          }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#666',
                marginBottom: '8px'
              }}>Industry</label>
              <select
                value={selectedIndustry}
                onChange={(e) => setSelectedIndustry(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '400',
                  outline: 'none',
                  cursor: 'pointer'
                }}
                onFocus={(e) => e.currentTarget.style.borderColor = '#004996'}
                onBlur={(e) => e.currentTarget.style.borderColor = '#e0e0e0'}
              >
                <option value="">Select Industry</option>
                {isIndustryLoading ? (
                  <option disabled>Loading...</option>
                ) : (
                  industryOptions.map((industry) => (
                    <option key={industry._id} value={industry._id}>
                      {industry.title}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#666',
                marginBottom: '8px'
              }}>Country</label>
              <Select
                options={options}
                value={value}
                onChange={(selectedOption) => setValue(selectedOption)}
                placeholder="Select Country"
                menuPlacement="bottom"
                styles={{
                  control: (base, state) => ({
                    ...base,
                    borderRadius: '8px',
                    border: state.isFocused ? '1px solid #004996' : '1px solid #e0e0e0',
                    fontSize: '14px',
                    minHeight: '46px',
                    boxShadow: 'none',
                    '&:hover': {
                      borderColor: '#004996'
                    }
                  }),
                  menu: (base) => ({
                    ...base,
                    borderRadius: '8px',
                    zIndex: 9999
                  })
                }}
              />
            </div>
          </div>

          {/* Tags Selection */}
          <div style={{ marginTop: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#666',
              marginBottom: '8px'
            }}>Tag</label>
            
            <select
              value={selectedTag}
              onChange={(e) => setSelectedTag(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '400',
                outline: 'none',
                cursor: 'pointer'
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = '#004996'}
              onBlur={(e) => e.currentTarget.style.borderColor = '#e0e0e0'}
            >
              <option value="">Select Tag</option>
              {isTagsLoading ? (
                <option disabled>Loading...</option>
              ) : (
                tagsOptions.map((tag) => (
                  <option key={tag._id} value={tag._id}>
                    {tag.name}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>

        {/* Conditional Upload Section Based on File Type */}
        {selectedFileType === 'image' && (
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            border: '1px solid #f0f0f0'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '500',
              color: '#333',
              marginBottom: '20px'
            }}>Upload Image</h3>

            <div
              onClick={() => !imagePreview && document.getElementById("imageInput").click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              style={{
                border: isDragging ? '2px dashed #004996' : imagePreview ? '2px solid #e0e0e0' : '2px dashed #e0e0e0',
                backgroundColor: isDragging ? '#f0f7ff' : '#fafafa',
                borderRadius: '12px',
                padding: '32px',
                transition: 'all 0.3s ease',
                minHeight: '300px',
                cursor: imagePreview ? 'default' : 'pointer',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {imagePreview ? (
                <div style={{ position: 'relative', width: '100%', maxWidth: '400px' }}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: '8px',
                      display: 'block'
                    }}
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveImage();
                    }}
                    style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      background: 'rgba(0,0,0,0.7)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      fontSize: '18px'
                    }}
                  >
                    ×
                  </button>
                </div>
              ) : (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📷</div>
                  <p style={{ fontSize: '16px', fontWeight: '500', color: '#333', marginBottom: '8px' }}>
                    Click or drag image to upload
                  </p>
                  <p style={{ fontSize: '13px', color: '#999' }}>
                    Supported formats: JPG, PNG, JPEG
                  </p>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                id="imageInput"
                style={{ display: "none" }}
                onChange={handleImageChange}
              />
            </div>
          </div>
        )}

        {selectedFileType === 'pdf' && (
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            border: '1px solid #f0f0f0'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '500',
              color: '#333',
              marginBottom: '20px'
            }}>Upload PDF Document</h3>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: '24px',
              marginBottom: '24px'
            }}>
              <div
                onClick={() => document.getElementById("fileInput").click()}
                onDragOver={handleFileDragOver}
                onDragLeave={handleFileDragLeave}
                onDrop={handleFileDrop}
                style={{
                  border: isFileDragging ? '2px dashed #004996' : '2px dashed #e0e0e0',
                  backgroundColor: isFileDragging ? '#f0f7ff' : '#fafafa',
                  borderRadius: '12px',
                  padding: '48px 32px',
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.3s ease'
                }}
              >
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄</div>
                <p style={{ fontSize: '16px', fontWeight: '500', color: '#333', marginBottom: '8px' }}>
                  Click or drag PDF file to upload
                </p>
                <p style={{ fontSize: '13px', color: '#999' }}>
                  Maximum file size: 20MB
                </p>
              </div>

              {/* Thumbnail Upload for PDF */}
              <div style={{ width: '200px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#666',
                  marginBottom: '8px'
                }}>Thumbnail Image</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="file"
                    id="thumbnailUpload"
                    accept=".png, .jpg, .jpeg"
                    onChange={(e) => readThumbnailURL(e.target)}
                    style={{ display: 'none' }}
                  />
                  <label
                    htmlFor="thumbnailUpload"
                    style={{
                      display: 'block',
                      width: '100%',
                      aspectRatio: '1',
                      border: '2px dashed #e0e0e0',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      background: thumbnailPreview ? `url(${thumbnailPreview}) center/cover` : '#fafafa',
                      position: 'relative'
                    }}
                  >
                    {!thumbnailPreview && (
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '32px', marginBottom: '4px' }}>+</div>
                        <div style={{ fontSize: '11px', color: '#999' }}>Add</div>
                      </div>
                    )}
                  </label>
                </div>
              </div>
            </div>

            <input
              type="file"
              id="fileInput"
              accept=".pdf"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />

            {uploadedFiles.length > 0 && (
              <div style={{
                borderTop: '1px solid #f0f0f0',
                paddingTop: '20px'
              }}>
                {uploadedFiles.map((file) => (
                  <div key={file.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '16px',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    marginBottom: '12px'
                  }}>
                    <div style={{ fontSize: '32px' }}>📄</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: '4px' }}>
                        {file.name}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {file.size} MB
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveFile(file.id)}
                      style={{
                        background: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        cursor: 'pointer',
                        color: '#e74c3c'
                      }}
                    >
                      <RiDeleteBin5Line size={18} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {selectedFileType === 'video' && (
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            border: '1px solid #f0f0f0'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '500',
              color: '#333',
              marginBottom: '20px'
            }}>Add YouTube Video</h3>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#666',
                marginBottom: '8px'
              }}>YouTube Video URL *</label>

              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: '24px',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <input
                    type="text"
                    placeholder="https://www.youtube.com/watch?v=..."
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = '#004996'}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#e0e0e0'}
                  />
                  <button
                    onClick={handleAddVideoUrl}
                    style={{
                      padding: '12px 24px',
                      background: 'linear-gradient(135deg, #004996 0%, #0066cc 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Add Video
                  </button>
                </div>

                {/* Thumbnail Upload for Video */}
                <div style={{ width: '200px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#666',
                    marginBottom: '8px'
                  }}>Thumbnail Image</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="file"
                      id="thumbnailUpload"
                      accept=".png, .jpg, .jpeg"
                      onChange={(e) => readThumbnailURL(e.target)}
                      style={{ display: 'none' }}
                    />
                    <label
                      htmlFor="thumbnailUpload"
                      style={{
                        display: 'block',
                        width: '100%',
                        aspectRatio: '1',
                        border: '2px dashed #e0e0e0',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        background: thumbnailPreview ? `url(${thumbnailPreview}) center/cover` : '#fafafa',
                        position: 'relative'
                      }}
                    >
                      {!thumbnailPreview && (
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '32px', marginBottom: '4px' }}>+</div>
                          <div style={{ fontSize: '11px', color: '#999' }}>Add</div>
                        </div>
                      )}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {bulkVideos.length > 0 && (
              <div style={{
                borderTop: '1px solid #f0f0f0',
                paddingTop: '20px'
              }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '500',
                  color: '#333',
                  marginBottom: '16px'
                }}>Video Preview</h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                  gap: '16px'
                }}>
                  {bulkVideos.map((vid, index) => {
                    const embedUrl = getEmbedUrl(vid);
                    return (
                      <div key={index} style={{
                        position: 'relative',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        border: '1px solid #e0e0e0',
                        background: '#000'
                      }}>
                        {embedUrl ? (
                          <iframe
                            width="100%"
                            height="200"
                            src={embedUrl}
                            title={`video-${index}`}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          ></iframe>
                        ) : (
                          <div style={{
                            height: '200px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#666'
                          }}>
                            Invalid URL
                          </div>
                        )}
                        <button
                          onClick={() => handleDeleteVideo(index)}
                          style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            background: 'rgba(0,0,0,0.7)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '8px',
                            cursor: 'pointer'
                          }}
                        >
                          <RiDeleteBin5Line size={16} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
          padding: '20px 0'
        }}>
          <button
            type="button"
            onClick={() => navigate('/mediauploads')}
            style={{
              padding: '12px 32px',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              background: '#fff',
              color: '#666',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f5f5f5';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#fff';
            }}
          >
            Cancel
          </button>
          <button
            onClick={saveData}
            type="button"
            disabled={isUpdating}
            style={{
              padding: '12px 32px',
              border: 'none',
              borderRadius: '8px',
              background: isUpdating ? '#ccc' : 'linear-gradient(135deg, #004996 0%, #0066cc 100%)',
              color: '#fff',
              fontSize: '14px',
              fontWeight: '500',
              cursor: isUpdating ? 'not-allowed' : 'pointer',
              boxShadow: '0 2px 8px rgba(0,73,150,0.2)',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!isUpdating) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,73,150,0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isUpdating) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,73,150,0.2)';
              }
            }}
          >
            {isUpdating ? 'Updating...' : 'Update Media'}
          </button>
        </div>
      </div>
    </>
  );
};

export default EditMultimedia;
